(() => {
  if (customElements.get('custom-design-upload')) return;

  const TYPES = /^image\/(jpeg|png|webp|heic|heif)$/;
  const EXT = /\.(jpe?g|png|webp|heic|heif)$/i;

  // The chosen file lives here so it survives the theme re-rendering the
  // product section when the customer switches model or MagSafe.
  const store = new Map();
  const live = new Set();

  // A re-render can also reset the input's value without touching the DOM,
  // so re-attach the file right before any click reaches Add to cart.
  document.addEventListener('click', () => live.forEach((el) => el.restore()), true);

  class CustomDesignUpload extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-cdu-input]');
      this.drop = this.querySelector('[data-cdu-drop]');
      this.preview = this.querySelector('[data-cdu-preview]');
      this.img = this.querySelector('[data-cdu-img]');
      this.nameEl = this.querySelector('[data-cdu-name]');
      this.errorEl = this.querySelector('[data-cdu-error]');
      if (!this.input) return;
      live.add(this);
      this.restore();
      this.observer = new MutationObserver(() => this.restore());
      this.observer.observe(this, { subtree: true, attributes: true, childList: true });
      if (this.bound) return;
      this.bound = true;

      this.input.addEventListener('change', () => this.onChange());
      this.input.addEventListener('invalid', (e) => {
        e.preventDefault();
        this.showError(this.dataset.msgRequired);
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      this.querySelector('[data-cdu-remove]')?.addEventListener('click', () => this.clear());

      ['dragenter', 'dragover'].forEach((t) =>
        this.drop.addEventListener(t, (e) => { e.preventDefault(); this.drop.classList.add('is-dragover'); })
      );
      ['dragleave', 'drop'].forEach((t) =>
        this.drop.addEventListener(t, () => this.drop.classList.remove('is-dragover'))
      );
      this.drop.addEventListener('drop', (e) => {
        e.preventDefault();
        const file = e.dataTransfer?.files?.[0];
        if (file) this.setFile(file);
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      live.delete(this);
    }

    get key() {
      return this.id;
    }

    onChange() {
      const file = this.input.files?.[0];
      if (file) this.setFile(file);
      else if (!store.has(this.key)) this.render(null);
      else this.restore();
    }

    validate(file) {
      const max = Number(this.dataset.maxMb || 20);
      if (!(TYPES.test(file.type) || EXT.test(file.name))) return this.dataset.msgType;
      if (file.size > max * 1024 * 1024) return (this.dataset.msgSize || '').replace('[size]', max);
      return '';
    }

    setFile(file) {
      const err = this.validate(file);
      if (err) {
        this.showError(err);
        this.input.value = '';
        if (store.has(this.key)) this.restore();
        return;
      }
      store.set(this.key, file);
      this.assign(file);
      this.render(file);
    }

    assign(file) {
      if (this.input.files?.[0] === file) return;
      const dt = new DataTransfer();
      dt.items.add(file);
      this.input.files = dt.files;
    }

    restore() {
      const file = store.get(this.key);
      if (!file || !this.input) return;
      if (this.input.files?.[0] !== file) this.assign(file);
      if (this.preview?.hidden) this.render(file);
    }

    clear() {
      store.delete(this.key);
      this.input.value = '';
      this.render(null);
    }

    render(file) {
      this.showError('');
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
      if (!file) {
        this.preview.hidden = true;
        this.drop.hidden = false;
        return;
      }
      this.objectUrl = URL.createObjectURL(file);
      this.img.src = this.objectUrl;
      // Browsers other than Safari can't show HEIC; keep the plain case colour then.
      this.img.onerror = () => this.img.removeAttribute('src');
      this.nameEl.textContent = file.name;
      this.preview.hidden = false;
      this.drop.hidden = true;
    }

    showError(msg) {
      this.errorEl.textContent = msg || '';
      this.errorEl.hidden = !msg;
      this.classList.toggle('has-error', Boolean(msg));
    }
  }

  customElements.define('custom-design-upload', CustomDesignUpload);
})();
